// src/components/WebsiteVisits/hooks/useWebsiteVisitsData.js

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import dayjs from "dayjs";
import api from "../../../../services/api/axios";

const normalizeGuid = (s = "") => s.trim().replace(/[{}]/g, "");

export default function useWebsiteVisitsData({
  accountGuid,
  organizationGuid,
  initialSince,
  initialUntil,
  q,
  industryFilter = [],
  countryFilter = [],
  stateFilter = [],
}) {
  const workingGuid = accountGuid || organizationGuid || "";
  const normGuid = useMemo(() => normalizeGuid(workingGuid), [workingGuid]);
  const resolvedInitialSince = useMemo(
    () =>
      initialSince && dayjs(initialSince).isValid()
        ? dayjs(initialSince).toISOString()
        : dayjs().subtract(30, "day").toISOString(),
    [initialSince],
  );
  const resolvedInitialUntil = useMemo(
    () =>
      initialUntil && dayjs(initialUntil).isValid()
        ? dayjs(initialUntil).toISOString()
        : dayjs().endOf("day").toISOString(),
    [initialUntil],
  );

  // ===============================
  // Date state
  // ===============================
  const [since, setSince] = useState(resolvedInitialSince);
  const [until, setUntil] = useState(resolvedInitialUntil);

  useEffect(() => {
    setSince(resolvedInitialSince);
  }, [resolvedInitialSince]);

  useEffect(() => {
    setUntil(resolvedInitialUntil);
  }, [resolvedInitialUntil]);

  // ===============================
  // Core data (GROUPED NOW)
  // ===============================
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // ===============================
  // Supporting datasets
  // ===============================
  const [summary, setSummary] = useState(null);
  const [burst, setBurst] = useState(null);
  const [clientTargetIndustries, setClientTargetIndustries] = useState([]);
  const [clientTargetIndustryCodes, setClientTargetIndustryCodes] = useState(
    [],
  );

  const [trafficSources, setTrafficSources] = useState([]);
  const [trafficSourcesLoading, setTrafficSourcesLoading] = useState(false);

  const scrollRef = useRef(null);
  const groupedRequestIdRef = useRef(0);
  const trafficSourcesRequestIdRef = useRef(0);

  const appendFilterValues = useCallback((params, key, values = []) => {
    for (const value of values || []) {
      const raw = typeof value === "string" ? value : value?.label || value?.name;
      const text = String(raw || "").trim();
      if (text) params.append(key, text);
    }
  }, []);

  const buildVisitFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (since) params.set("since", since);
    if (until) params.set("until", until);
    if (q?.trim()) params.set("q", q.trim());
    appendFilterValues(params, "industries", industryFilter);
    appendFilterValues(params, "countries", countryFilter);
    appendFilterValues(params, "states", stateFilter);
    return params;
  }, [
    since,
    until,
    q,
    industryFilter,
    countryFilter,
    stateFilter,
    appendFilterValues,
  ]);

  // ===============================
  // Grouped visits from the synced website_visits table
  // ===============================
  const fetchGrouped = useCallback(async (signal) => {
    if (!normGuid) return null;

    const params = buildVisitFilterParams();

    const { data } = await api.get(
      `/website-visits/${normGuid}/grouped?${params.toString()}`,
      { signal, timeout: 300000 }
    );

    return data;
  }, [
    normGuid,
    buildVisitFilterParams,
  ]);

  const refreshGrouped = useCallback(async () => {
    if (!normGuid) return;

    const requestId = groupedRequestIdRef.current + 1;
    groupedRequestIdRef.current = requestId;

    try {
      setLoading(true);
      setErr(null);
      const data = await fetchGrouped();
      if (requestId !== groupedRequestIdRef.current) return;
      setItems(data || []);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (requestId !== groupedRequestIdRef.current) return;
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail || e?.message || "Request failed";
      setErr({ status, detail });
    } finally {
      if (requestId === groupedRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [fetchGrouped, normGuid]);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const requestId = groupedRequestIdRef.current + 1;
    groupedRequestIdRef.current = requestId;

    setItems([]);
    setErr(null);

    (async () => {
      if (!normGuid) return;

      try {
        setLoading(true);

        const data = await fetchGrouped(controller.signal);

        if (!mounted || requestId !== groupedRequestIdRef.current) return;

        setItems(data || []);

      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (!mounted) return;

        const status = e?.response?.status;
        const detail =
          e?.response?.data?.detail || e?.message || "Request failed";

        setErr({ status, detail });
        setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [normGuid, fetchGrouped]);

  // ===============================
  // Recent summary from the synced website_visits table
  // ===============================
  useEffect(() => {
    if (!normGuid) return;
    let mounted = true;
    const controller = new AbortController();

    api
      .get(`/website-visits/${normGuid}/summary?${buildVisitFilterParams().toString()}`, {
        signal: controller.signal,
      })
      .then((res) => {
        if (mounted) setSummary(res.data);
      })
      .catch((e) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (mounted) setSummary(null);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [normGuid, buildVisitFilterParams]);

  // ===============================
  // Legacy all-time burst data still backed by Dynamics
  // ===============================
  useEffect(() => {
    if (!normGuid) return;
    let mounted = true;
    const controller = new AbortController();

    api
      .get(`/website-visits/${normGuid}/burst-all-time`, {
        signal: controller.signal,
      })
      .then((res) => {
        if (mounted) setBurst(res.data);
      })
      .catch((e) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (mounted) setBurst(null);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [normGuid]);

  // ===============================
  // Target industries
  // ===============================
  useEffect(() => {
    if (!normGuid) return;
    let mounted = true;
    const controller = new AbortController();

    api
      .get(`/website-visits/${normGuid}/target-industries`, {
        signal: controller.signal,
      })
      .then((res) => {
        if (!mounted) return;
        const items = res.data?.items || [];
        const codes = [];
        for (const row of items) {
          if (row.code) codes.push(String(row.code));
        }
        setClientTargetIndustries(items);
        setClientTargetIndustryCodes(codes);
      })
      .catch((e) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (!mounted) return;
        setClientTargetIndustries([]);
        setClientTargetIndustryCodes([]);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [normGuid]);

  // ===============================
  // Traffic sources still backed by Dynamics
  // ===============================
  const fetchTrafficSources = useCallback(
    async (companyKey) => {
      const requestId = trafficSourcesRequestIdRef.current + 1;
      trafficSourcesRequestIdRef.current = requestId;

      if (!normGuid || !companyKey) {
        setTrafficSources([]);
        setTrafficSourcesLoading(false);
        return;
      }

      setTrafficSourcesLoading(true);

      try {
        const res = await api.get(
          `/website-visits/${normGuid}/company-traffic-sources`,
          {
            params: {
              selected_company_key: companyKey,
              since: since || undefined,
              until: until || undefined,
            },
          },
        );

        if (requestId !== trafficSourcesRequestIdRef.current) return;
        setTrafficSources(res.data?.source_breakdown || []);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (requestId !== trafficSourcesRequestIdRef.current) return;
        setTrafficSources([]);
      } finally {
        if (requestId === trafficSourcesRequestIdRef.current) {
          setTrafficSourcesLoading(false);
        }
      }
    },
    [normGuid, since, until],
  );

  // ===============================
  // Update tags on grouped DB-backed rows
  // ===============================
  const updateVisitTags = useCallback((updatedVisit) => {
    const targetVisitId = updatedVisit?.visit_id || updatedVisit?.id;
    if (!targetVisitId) return;

    setItems((prev) =>
      (prev || []).map((item) => {
        if (!item) return item;

        const row = item?.representative || item;
        const rowVisitId = row?.visit_id || row?.id;

        if (rowVisitId !== targetVisitId) {
          return item;
        }

        const nextRow = {
          ...row,
          ...updatedVisit,
          user_tags: updatedVisit?.user_tags ?? row?.user_tags ?? [],
        };

        if (item?.representative) {
          return {
            ...item,
            representative: nextRow,
            user_tags: nextRow.user_tags,
          };
        }

        return nextRow;
      }),
    );
  }, []);

  return {
    normGuid,
    since,
    until,
    setSince,
    setUntil,

    items,
    loading,
    err,
    summary,
    burst,
    clientTargetIndustries,
    clientTargetIndustryCodes,

    scrollRef,

    trafficSources,
    trafficSourcesLoading,
    fetchTrafficSources,
    updateVisitTags,
    refreshGrouped,
  };
}
